import React, { FC, useEffect, useRef, useState } from "react";
import { Locale, LOCALES, useLocale } from "./i18n/locale";

const LABELS: Record<Locale, { short: string; full: string }> = {
  en: { short: "EN", full: "English" },
  fr: { short: "FR", full: "Français" },
};

export interface LanguageSwitcherProps {
  /** "light" inverts the control for the dark hero on step 1. */
  variant?: "light" | "dark";
  className?: string;
}

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  variant = "dark",
  className = "",
}) => {
  const { locale, setLocale, t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const choose = (next: Locale) => {
    setLocale(next);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t.common.language}
        className={`flex items-center gap-1 rounded-full px-2 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000 ${
          variant === "light"
            ? "text-white hover:bg-white/15"
            : "text-neutral-600 hover:bg-white/70"
        }`}
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a15 15 0 010 18 15 15 0 010-18z" />
        </svg>
        {LABELS[locale].short}
        <svg
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        // Right-aligned: the control sits in the top-right corner, so a
        // left-aligned menu would hang off the screen on a phone.
        <ul
          role="menu"
          className="absolute right-0 z-20 mt-1 min-w-[8rem] overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-lg"
        >
          {LOCALES.map((option) => (
            <li key={option} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={option === locale}
                lang={option}
                onClick={() => choose(option)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-cream-100 focus:bg-cream-100 focus:outline-none ${
                  option === locale
                    ? "font-semibold text-navy-900"
                    : "text-neutral-600"
                }`}
              >
                {LABELS[option].full}
                {option === locale && (
                  <svg
                    className="h-3.5 w-3.5 flex-none text-primary-6000"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
